import {
  ActivityFlowSection,
  KsAdditionalInfoSection,
  KsAdditionalInfoSectionBefore2025,
  KsBasicInfoSection,
  KsBasicInfoSectionBefore2025,
  KsFundAdditionalIncomeAndExpenditureInfoSection,
  KsFundAdditionalIncomeAndExpenditureInfoSectionBefore2025,
  KsFundBusinessInfoSection,
  KsInspectionEvaluationSection,
  KsInspectionEvaluationSectionBefore2025,
  KsOwnershipRatioSection,
  LedgerPage,
  RelatedMaterialSection,
} from '@/features/mold/components';
import { PaymentSection } from '@/features/mold/components/pdf/rs-ledger/payment-section/PaymentSection';
import { PAPER_FILE_SIZE } from '@/features/mold/utils';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { FC } from 'react';
import type { useProjectLedgerData } from '../_hooks/useProjectLedgerData';

type Props = {
  projectId: string;
  ledgerData?: ReturnType<typeof useProjectLedgerData>;
  pageId?: string;
};

export const KsProjectLedgerPage: FC<Props> = ({ ledgerData, pageId }) => {
  // ledgerDataが渡されない場合は、以前の実装にフォールバック（互換性のため）
  if (!ledgerData) {
    return null;
  }

  const {
    isLoading,
    project,
    ministries,
    activityTreeOption,
    nestedConsecutiveIndicators,
    relatedMaterials,
    paymentGroups,
    paymentGraph,
    payments,
    data: { ks },
  } = ledgerData;

  // ローディング中または必要なデータがない場合はnullを返す
  if (isLoading || !project || !ministries) {
    return null;
  }

  return (
    <LedgerPage width={PAPER_FILE_SIZE.A4.height} isLoading={false} id={pageId}>
      {isBefore2025(project) ? (
        <KsBasicInfoSectionBefore2025 ledgerData={ledgerData} />
      ) : (
        <KsBasicInfoSection ledgerData={ledgerData} />
      )}

      {isBefore2025(project) ? (
        <KsAdditionalInfoSectionBefore2025 project={project} />
      ) : (
        <KsAdditionalInfoSection project={project} />
      )}
      {isBefore2025(project) ? (
        <>
          <KsFundAdditionalIncomeAndExpenditureInfoSectionBefore2025
            project={project}
          />
          <ActivityFlowSection
            project={project}
            nestedConsecutiveIndicators={nestedConsecutiveIndicators}
            tree={activityTreeOption.tree.data}
            selectOptions={activityTreeOption.selectOptions}
          />
        </>
      ) : (
        <>
          <ActivityFlowSection
            project={project}
            nestedConsecutiveIndicators={nestedConsecutiveIndicators}
            tree={activityTreeOption.tree.data}
            selectOptions={activityTreeOption.selectOptions}
          />
          <KsFundAdditionalIncomeAndExpenditureInfoSection project={project} />
        </>
      )}
      <KsFundBusinessInfoSection
        fiscalYear={project?.fiscalYear}
        fundBusinesses={ks.fundBusinesses}
      />
      {!isBefore2025(project) && <KsOwnershipRatioSection project={project} />}
      {isBefore2025(project) ? (
        <KsInspectionEvaluationSectionBefore2025 project={project} />
      ) : (
        <KsInspectionEvaluationSection project={project} />
      )}
      <PaymentSection
        project={project}
        paymentGraph={paymentGraph}
        paymentGroups={paymentGroups}
        payments={payments}
      />
      <RelatedMaterialSection relatedMaterials={relatedMaterials} />
    </LedgerPage>
  );
};
